import React, { useEffect, useRef, useState } from 'react';
import globalImageCache from './globalImageCache';
import '../../styles/contents/shortFormQuiz/ShortFormQuizBubbleWrap.css';
const bubbleLongUrl = '/assets/images/contents/shortForm/bubble/Bubble_long.png';
const bubbleLongPoppedUrl = '/assets/images/contents/shortForm/bubble/Bubble_Long_Poped.png';
const bubbleSuccessUrl = '/assets/images/contents/shortForm/bubble/Bubble_Success.png';
const bubbleFailUrl = '/assets/images/contents/shortForm/bubble/Bubble_Fail.png';

const ShortFormQuizBubbleWrap = ({ 
  stagePhase, 
  localStage, 
  quiz, 
  setStagePhase, 
  correctCountRef, 
  totalQuestionsRef, 
  answersRef, 
  wrongIndexesRef, 
  stageAnsweredRef,
  feedbackActive,
  feedbackIsSuccess,
  feedbackRect,
  setFeedbackActive,
  setFeedbackIsSuccess,
  setFeedbackRect,
  setIsGameInProgress,
  canvasRef,
  currentQuestion,
  bubbleImage,
  bubbleFilledImage,
  // 새로 추가된 props
  includeTutorial,
  isTutorialStage
}) => {
  const rafRef = useRef(null);
  const [bubbles, setBubbles] = useState([]);
  const bubblesRef = useRef([]);
  const imagesRef = useRef({ circle: null, popped: null, long: null, longPopped: null, success: null, fail: null, ready: false });
  const [optionMeta, setOptionMeta] = useState({ type: 'tutorial', correctIndex: 0, selectedIndex: null });

  useEffect(() => {
    if (globalImageCache.isReadyToUse()) {
      // 전역 캐시에서 이미지 가져오기
      const circle = globalImageCache.get(bubbleImage);
      const popped = globalImageCache.get(bubbleFilledImage);
      const longImg = globalImageCache.get(bubbleLongUrl);
      const longPopped = globalImageCache.get(bubbleLongPoppedUrl);
      const success = globalImageCache.get(bubbleSuccessUrl);
      const fail = globalImageCache.get(bubbleFailUrl);
      
      imagesRef.current = { circle, popped, long: longImg, longPopped, success, fail, ready: true };
    } else {
      // 전역 캐시가 준비되지 않았으면 기존 방식으로 로딩
      
      // Image preloading logic (fallback)
      const circle = new Image(); circle.src = bubbleImage;
      const popped = new Image(); popped.src = bubbleFilledImage;
      const longImg = new Image(); longImg.src = bubbleLongUrl;
      const longPopped = new Image(); longPopped.src = bubbleLongPoppedUrl;
      const success = new Image(); success.src = bubbleSuccessUrl;
      const fail = new Image(); fail.src = bubbleFailUrl;
      let loaded = 0;
      const onLoaded = () => { 
        loaded += 1; 
        if (loaded >= 6) { 
          imagesRef.current.ready = true;
        } 
      };
      const onErr = (src) => { 
        console.error(`뽁뽁이 이미지 로딩 실패: ${src}`); 
        onLoaded(); // 에러도 완료로 카운트
      };
      [circle, popped, longImg, longPopped, success, fail].forEach((img) => { 
        img.onload = onLoaded; 
        img.onerror = () => onErr(img.src); 
      });
      imagesRef.current = { circle, popped, long: longImg, longPopped, success, fail, ready: false };
    }
  }, [bubbleImage, bubbleFilledImage, globalImageCache]);

  useEffect(() => {
    if (stagePhase !== 'pre' && stagePhase !== 'play' && stagePhase !== 'feedback') {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const parent = canvas.parentElement;
    const width = parent?.clientWidth;
    const height = parent?.clientHeight;

    if (!width || !height) return;
    
    if (stagePhase === 'pre' || stagePhase === 'play') {
      canvas.width = width;
      canvas.height = height;
    }

    if ((stagePhase === 'pre' || stagePhase === 'play') && isTutorialStage) {
      const cols = 4, rows = 7;
      const topUiRatio = 0.22;
      const drawingAreaHeight = canvas.height * (1 - topUiRatio);
      
      const rByHeight = Math.floor(drawingAreaHeight / (rows * 2));
      const rByWidth = Math.floor(canvas.width / 10);
      const radius_for_layout = Math.max(8, Math.min(rByWidth, rByHeight));
      const radius = radius_for_layout * 0.85;

      const stepX = 2 * radius_for_layout;
      const marginX = Math.max(radius_for_layout, (canvas.width - (4.5 * stepX)) / 2 + stepX / 2);

      const stepY = drawingAreaHeight / rows;
      const marginY = (canvas.height * topUiRatio) + (stepY / 2);
      
      const list = [];
      for (let r = 0; r < rows; r++) {
        const offset = (r % 2 === 1) ? 0.5 : 0;
        for (let c = 0; c < cols; c++) {
          const x = marginX + (c + offset) * stepX;
          const y = marginY + r * stepY;
          list.push({ id: `b-${r}-${c}`, type: 'circle', x, y, r: radius, popped: true, active: false });
        }
      }
      
      const indices = list.map((_, i) => i).sort(() => Math.random() - 0.5);
      const targetCount = Math.min(5, list.length);
      for (let i = 0; i < targetCount; i++) {
        const idx = indices[i];
        list[idx] = { ...list[idx], popped: false, active: true };
      }
      setBubbles(list);
      bubblesRef.current = list;
      setOptionMeta({ type: 'tutorial' });
    } else if ((stagePhase === 'pre' || stagePhase === 'play') && !isTutorialStage) {
      const cols = 4, rows = 7;
      const topUiRatio = 0.22;
      const drawingAreaHeight = canvas.height * (1 - topUiRatio);

      const rByHeight = Math.floor(drawingAreaHeight / (rows * 2));
      const rByWidth = Math.floor(canvas.width / 10);
      const radius_for_layout = Math.max(8, Math.min(rByWidth, rByHeight));
      const radius = radius_for_layout * 0.85;

      const stepX = 2 * radius_for_layout;
      const marginX = Math.max(radius_for_layout, (canvas.width - (4.5 * stepX)) / 2 + stepX / 2);
      
      const stepY = drawingAreaHeight / rows;
      const marginY = (canvas.height * topUiRatio) + (stepY / 2);
      
      const allRows = Array.from({ length: rows }, (_, i) => i);
      const selectedRows = allRows.sort(() => Math.random() - 0.5).slice(0, 4).sort((a, b) => a - b);
      
      // 퀴즈 데이터 로드 - 수정된 인덱스 계산
      const qList = Array.isArray(quiz?.questions) ? quiz.questions : [];
      let qIndexRaw;
      
      if (includeTutorial) {
        // 튜토리얼 포함: localStage 5 = qIndex 0
        qIndexRaw = localStage - 5;
      } else {
        // 튜토리얼 없음: localStage 1 = qIndex 0
        qIndexRaw = localStage - 1;
      }
      
      let options = ['A', 'B', 'C', 'D'];
      let correctIndex = 0;
      
      // 유효한 문제 범위 내에서만 처리
      if (qList.length > 0 && qIndexRaw >= 0 && qIndexRaw < qList.length) {
        const q = qList[qIndexRaw];
        if (q && Array.isArray(q.options)) {
          options = q.options.slice(0, 4).map(String);
        }
        if (typeof q?.answerIndex === 'number') {
          correctIndex = q.answerIndex;
        }
      }
      
      const list = [];
      for (let r = 0; r < rows; r++) {
        if (selectedRows.includes(r)) continue;
        const offset = (r % 2 === 1) ? 0.5 : 0;
        for (let c = 0; c < cols; c++) {
          const x = marginX + (c + offset) * stepX;
          const y = marginY + r * stepY;
          list.push({ id: `d-${r}-${c}`, type: 'circle', x, y, r: radius, popped: true, deco: true });
        }
      }
      
      selectedRows.forEach((r, idx) => {
        const y = marginY + stepY * r;
        const w = 8 * radius_for_layout;
        const h = Math.max(radius_for_layout * 1.8, stepY * 0.8);
        const isOdd = r % 2 === 1;
        const left = isOdd ? marginX : (marginX - radius_for_layout);
        const x = left + w / 2;
        list.push({ id: `L-${r}`, type: 'long', x, y, w, h, popped: false, optionIndex: idx, text: options[idx], correct: idx === correctIndex });
      });
      
      setBubbles(list);
      bubblesRef.current = list;
      setOptionMeta({ type: 'long', correctIndex, selectedIndex: null });
    }

    const ctx = canvas.getContext('2d');
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // 배경색 설정
      ctx.fillStyle = '#f97316';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const arr = bubblesRef.current || [];
      arr.forEach((b) => {
        if (b.type === 'circle') {
          const size = b.r * 2;
          const img = (b.popped ? imagesRef.current.popped : imagesRef.current.circle);
          if (img && img.complete && imagesRef.current.ready) {
            ctx.drawImage(img, b.x - size / 2, b.y - size / 2, size, size);
          }
        } else if (b.type === 'long') {
          const x = b.x - b.w / 2, y = b.y - b.h / 2;
          const img = b.popped ? imagesRef.current.longPopped : imagesRef.current.long;
          if (img && img.complete && imagesRef.current.ready) {
            ctx.drawImage(img, x, y, b.w, b.h);
          }
          
          // 개선된 텍스트 렌더링 로직
          const text = b.text || '';
          ctx.fillStyle = '#0f172a';
          ctx.textAlign = 'center'; 
          ctx.textBaseline = 'middle';
          
          // 버블 크기에 따른 최대 폰트 크기 설정 (더 큰 기본 크기)
          let baseFontSize = Math.max(14, Math.round(b.h * 0.4));
          const maxWidth = b.w * 0.85; // 여백을 더 확보
          const maxLines = 2; // 최대 2줄로 제한
          
          // 텍스트 길이에 따른 자동 줄바꿈 처리
          const splitTextIntoLines = (text, maxWidth, fontSize) => {
            ctx.font = `bold ${fontSize}px Arial`;
            
            // 텍스트가 한 줄에 맞으면 그대로 반환
            if (ctx.measureText(text).width <= maxWidth) {
              return [text];
            }
            
            // 한글/영어 혼재 텍스트를 위한 개선된 분할 로직
            const chars = text.split('');
            const lines = [];
            let currentLine = '';
            
            for (let i = 0; i < chars.length; i++) {
              const testLine = currentLine + chars[i];
              const testWidth = ctx.measureText(testLine).width;
              
              if (testWidth > maxWidth && currentLine.length > 0) {
                lines.push(currentLine);
                currentLine = chars[i];
                
                // 2줄 제한
                if (lines.length >= maxLines - 1) {
                  // 남은 글자들을 현재 줄에 추가하되, 너무 길면 말줄임표 처리
                  const remainingChars = chars.slice(i);
                  let remainingText = remainingChars.join('');
                  
                  while (ctx.measureText(currentLine + remainingText).width > maxWidth && remainingText.length > 1) {
                    remainingText = remainingText.slice(0, -1);
                  }
                  
                  if (remainingText.length < remainingChars.length) {
                    currentLine += remainingText.slice(0, -1) + '...';
                  } else {
                    currentLine += remainingText;
                  }
                  break;
                }
              } else {
                currentLine = testLine;
              }
            }
            
            if (currentLine) {
              lines.push(currentLine);
            }
            
            return lines;
          };
          
          // 폰트 크기를 조정하면서 최적의 줄바꿈 찾기
          let fontSize = baseFontSize;
          let lines = [];
          
          // 먼저 기본 폰트 크기로 줄바꿈 시도
          lines = splitTextIntoLines(text, maxWidth, fontSize);
          
          // 2줄일 때 폰트 크기를 살짝 줄이기 (더 읽기 쉽게)
          if (lines.length === 2) {
            fontSize = Math.max(10, fontSize * 0.9); // 기본 크기의 90%로 줄임
            lines = splitTextIntoLines(text, maxWidth, fontSize);
          }
          
          // 2줄을 초과하면 폰트 크기 더 줄이기
          while (lines.length > maxLines && fontSize > 10) {
            fontSize = Math.max(10, fontSize - 1);
            lines = splitTextIntoLines(text, maxWidth, fontSize);
          }
          
          // 최종 폰트 설정
          ctx.font = `bold ${fontSize}px Arial`;
          
          // 줄 간격과 전체 높이 계산
          const lineHeight = fontSize * 1.3; // 줄 간격을 약간 늘림
          const totalHeight = lines.length * lineHeight;
          const startY = b.y - (totalHeight / 2) + (lineHeight / 2);
          
          // 각 줄 그리기
          lines.forEach((line, index) => {
            const y = startY + (index * lineHeight);
            ctx.fillText(line, b.x, y);
          });
        }
      });

      // 피드백 오버레이 그리기
      if (stagePhase === 'feedback' && feedbackActive && imagesRef.current.ready) {
        ctx.fillStyle = 'rgba(107, 114, 128, 0.5)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        const img = feedbackIsSuccess ? imagesRef.current.success : imagesRef.current.fail;
        if (img && img.complete) {
          const size = Math.min(canvas.width, canvas.height) * 0.3;
          const cx = canvas.width / 2;
          const cy = canvas.height / 2;
          ctx.drawImage(img, cx - size / 2, cy - size / 2, size, size);
        }
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();

    const resolvePoint = (e) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      if (e.touches && e.touches[0]) {
        return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY, isTouch: true };
      }
      return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY, isTouch: e.pointerType === 'touch' };
    };

    const handlePointer = (evt) => {
      if (evt.cancelable) evt.preventDefault();
      const { x: px, y: py, isTouch } = resolvePoint(evt);
      
      if (isTutorialStage) {
        setBubbles((prev) => {
          const next = prev.map((b) => {
            if (b.type !== 'circle') return b;
            const boost = isTouch ? 1.25 : 1;
            const hit = Math.hypot(px - b.x, py - b.y) <= b.r * boost;
            if (hit && !b.popped && b.active) {
              return { ...b, popped: true };
            }
            return b;
          });
          bubblesRef.current = next;
          
          const remaining = next.filter(b => b.type === 'circle' && b.active && !b.popped).length;
          if (remaining === 0) {
            const cx = canvasRef.current?.width / 2 || 200;
            const cy = canvasRef.current?.height / 2 || 150;
            setFeedbackActive(true);
            setFeedbackIsSuccess(true);
            const base = Math.min(canvasRef.current?.width || 600, canvasRef.current?.height || 400) * 0.3;
            setFeedbackRect({ x: cx, y: cy, w: base, h: base });
            setStagePhase('feedback');
          }
          return next;
        });
      } else {
        setBubbles((prev) => {
          const next = prev.map((b) => {
            if (b.type !== 'long') return b;
            const x = b.x - b.w / 2, y = b.y - b.h / 2;
            const hit = px >= x && px <= x + b.w && py >= y && py <= y + b.h;
            if (hit && !b.popped) {
              setOptionMeta((m) => ({ ...m, selectedIndex: b.optionIndex }));
              
              const isCorrect = b.correct === true;
              if (!stageAnsweredRef.current) {
                if (isCorrect) correctCountRef.current += 1;
                
                // 수정된 인덱스 계산으로 답안 기록
                let qIndexRaw;
                if (includeTutorial) {
                  qIndexRaw = localStage - 5;
                } else {
                  qIndexRaw = localStage - 1;
                }
                
                try {
                  const qList = Array.isArray(quiz?.questions) ? quiz.questions : [];
                  // 유효한 범위 내에서만 처리
                  if (qIndexRaw >= 0 && qIndexRaw < qList.length) {
                    if (!Array.isArray(answersRef.current)) answersRef.current = [];
                    answersRef.current[qIndexRaw] = b.optionIndex;
                  }
                  if (!isCorrect && qIndexRaw >= 0 && qIndexRaw < qList.length) {
                    if (!Array.isArray(wrongIndexesRef.current)) wrongIndexesRef.current = [];
                    const oneBased = qIndexRaw + 1;
                    if (!wrongIndexesRef.current.includes(oneBased)) wrongIndexesRef.current.push(oneBased);
                  }
                } catch (_) {}
                stageAnsweredRef.current = true;
                // 답안 선택 시 게임 진행 상태 해제
                if (setIsGameInProgress) setIsGameInProgress(false);
              }
              
              const cw = canvasRef.current?.width || 600;
              const ch = canvasRef.current?.height || 400;
              const base = Math.min(cw, ch) * 0.3;
              setFeedbackActive(true);
              setFeedbackIsSuccess(isCorrect);
              setFeedbackRect({ x: cw / 2, y: ch / 2, w: base, h: base });
              setStagePhase('feedback');
              return { ...b, popped: true };
            }
            return b;
          });
          bubblesRef.current = next;
          return next;
        });
      }
    };

    canvas.addEventListener('click', handlePointer, { passive: false });
    canvas.addEventListener('touchstart', handlePointer, { passive: false });

    return () => {
      canvas.removeEventListener('click', handlePointer);
      canvas.removeEventListener('touchstart', handlePointer);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [stagePhase, localStage, bubbles.length, quiz?.questions, correctCountRef, answersRef, wrongIndexesRef, stageAnsweredRef, setStagePhase, setOptionMeta, currentQuestion, isTutorialStage, includeTutorial]);

  return (
    <div style={{ 
      width: '100%', 
      height: '100%', 
      position: 'relative',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <canvas 
        ref={canvasRef} 
        style={{ 
          width: '100%', 
          height: '100%', 
          touchAction: 'none', 
          WebkitTapHighlightColor: 'transparent', 
          pointerEvents: stagePhase === 'play' ? 'auto' : 'none',
          display: 'block'
        }} 
      />
    </div>
  );
};

export default ShortFormQuizBubbleWrap;
